/**
 * WhatsAppAccountsPage — `/whatsapp/accounts`
 *
 * Listado de cuentas de WhatsApp vinculadas a la marca + CTA para vincular
 * una nueva (abre LinkAccountModal con QR mock).
 *
 * Si no hay cuentas vinculadas, muestra un empty state grande que invita
 * a vincular la primera.
 *
 * Pollea cada 8s mientras hay alguna cuenta en estado transitorio
 * (pending_qr / reconnecting) — así actualizamos sin interacción.
 */

import { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, AlertCircle, Smartphone } from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { ConfirmDangerModal } from "../../../shared/components/ui/ConfirmDangerModal";
import { toast } from "../../../shared/hooks/useToast";
import { whatsappAccountsApi } from "../api";
import { useIntegracion } from "../../../hooks/useIntegracion";
import WhatsAppSubNav from "../components/WhatsAppSubNav";
import AccountCard from "../components/AccountCard";
import LinkAccountModal from "../components/LinkAccountModal";
import type { WhatsAppAccount } from "../types";

export default function WhatsAppAccountsPage() {
    const [accounts, setAccounts] = useState<WhatsAppAccount[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const { locales, getLocales } = useIntegracion();

    const fetchAccounts = useCallback(async () => {
        const res = await whatsappAccountsApi.list();
        if (res.success) {
            setAccounts(res.data);
            setError(null);
        } else {
            setError(res.error || "Error al cargar cuentas");
        }
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchAccounts();
            setLoading(false);
        })();
        if (locales.length === 0) getLocales();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Poll mientras haya cuentas en estado transitorio */
    useEffect(() => {
        const hasTransient = accounts.some(
            (a) => a.status === "pending_qr" || a.status === "reconnecting",
        );
        if (!hasTransient) return;
        const interval = setInterval(() => {
            fetchAccounts();
        }, 8000);
        return () => clearInterval(interval);
    }, [accounts, fetchAccounts]);

    const handleSetDefault = async (id: string) => {
        const res = await whatsappAccountsApi.setDefault(id);
        if (res.success) await fetchAccounts();
    };
    const handleReconnect = async (id: string) => {
        const res = await whatsappAccountsApi.reconnect(id);
        if (res.success) {
            await fetchAccounts();
            // Poll una vez después de 2s para capturar el cambio a connected
            setTimeout(() => fetchAccounts(), 2000);
        }
    };
    const [pendingUnlink, setPendingUnlink] = useState<string | null>(null);
    const [unlinkBusy, setUnlinkBusy] = useState(false);
    const handleUnlink = (id: string) => setPendingUnlink(id);
    const confirmUnlink = async () => {
        if (!pendingUnlink) return;
        setUnlinkBusy(true);
        const res = await whatsappAccountsApi.remove(pendingUnlink);
        if (res.success) {
            await fetchAccounts();
            toast.success("Cuenta desvinculada");
        }
        setUnlinkBusy(false);
        setPendingUnlink(null);
    };
    const handlePause = async (id: string) => {
        const res = await whatsappAccountsApi.pause(id);
        if (res.success) await fetchAccounts();
    };
    const handleActivate = async (id: string) => {
        const res = await whatsappAccountsApi.activate(id);
        if (res.success) await fetchAccounts();
    };

    const connectedCount = accounts.filter((a) => a.status === "connected").length;
    const hasAccounts = accounts.length > 0;

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-7xl space-y-6">
                <WhatsAppSubNav active="accounts" accountsCount={accounts.length} />

                <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                            Cuentas vinculadas
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">
                            Cada cuenta es un número real de WhatsApp escaneado por QR.
                            {hasAccounts && (
                                <>
                                    {" "}
                                    <span className="font-medium text-slate-700">
                                        {connectedCount} de {accounts.length} activas
                                    </span>
                                    .
                                </>
                            )}
                        </p>
                    </div>
                    {hasAccounts && (
                        <Button
                            variant="primary"
                            onClick={() => setModalOpen(true)}
                        >
                            <Plus size={14} />
                            Vincular nuevo número
                        </Button>
                    )}
                </div>

                {error && (
                    <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700">
                        <AlertCircle size={18} />
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-20 text-slate-500 text-sm">
                        <Loader2 size={18} className="animate-spin mr-2" />
                        Cargando cuentas…
                    </div>
                ) : !hasAccounts ? (
                    <EmptyState onLink={() => setModalOpen(true)} />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                        {accounts.map((a) => (
                            <AccountCard
                                key={a.id}
                                account={a}
                                localName={
                                    locales.find((l) => l._id === a.localId)
                                        ?.nameLocal
                                }
                                onSetDefault={handleSetDefault}
                                onReconnect={handleReconnect}
                                onUnlink={handleUnlink}
                                onPause={handlePause}
                                onActivate={handleActivate}
                            />
                        ))}
                    </div>
                )}

                <LinkAccountModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    onLinked={fetchAccounts}
                    locales={locales}
                />

                {pendingUnlink && (
                    <ConfirmDangerModal
                        title="Desvincular cuenta"
                        description="¿Desvincular esta cuenta de WhatsApp? Las campañas activas que la usen se pausarán automáticamente."
                        confirmLabel="Desvincular"
                        busyLabel="Desvinculando…"
                        onConfirm={confirmUnlink}
                        onCancel={() => setPendingUnlink(null)}
                        busy={unlinkBusy}
                    />
                )}
            </div>
        </div>
    );
}

function EmptyState({ onLink }: { onLink: () => void }) {
    return (
        <div className="bg-gradient-to-br from-green-50/50 to-white border-2 border-dashed border-green-200 rounded-2xl p-12 text-center max-w-2xl mx-auto">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-green-100 text-green-600 flex items-center justify-center mb-4">
                <Smartphone size={28} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
                Vinculá tu primer número de WhatsApp
            </h2>
            <p className="text-sm text-slate-600 max-w-md mx-auto mb-6">
                Escaneás un QR desde tu celular (igual que WhatsApp Web). Después podés
                lanzar campañas, automatizar respuestas y conectar objetivos.
            </p>
            <Button variant="primary" onClick={onLink}>
                <Plus size={14} />
                Vincular primer número
            </Button>
            <p className="text-[11px] text-slate-400 mt-4">
                Toma menos de 30 segundos · Sin proceso de aprobación · Sin costo por mensaje
            </p>
        </div>
    );
}
